import React from 'react';

/**
 * ClinicalFlow branded loader.
 *
 * iter164a — Replaces the generic Loader2 spinner across the app's full-page
 * and section-level loading states with the ClinicalFlow logo wrapped in a
 * soft pulsing glow and a thin rotating accent ring. Designed to feel calm,
 * medical, and on-brand.
 *
 * Usage:
 *   <BrandLoader />                       // section-level (default)
 *   <BrandLoader size="lg" label="Loading dashboard…" />
 *   <BrandLoader size="sm" />             // small inline (modals, dialogs)
 */
export function BrandLoader({ size = 'md', label, className = '', fullScreen = false }) {
  const sizes = {
    sm: { box: 48, logo: 28, ring: 'border-2', text: 'text-xs' },
    md: { box: 80, logo: 44, ring: 'border-2', text: 'text-sm' },
    lg: { box: 112, logo: 64, ring: 'border-[3px]', text: 'text-base' },
  };
  const s = sizes[size] || sizes.md;

  const container = (
    <div
      className={`flex flex-col items-center justify-center gap-4 ${className}`}
      role="status"
      aria-live="polite"
      data-testid="brand-loader"
    >
      <div className="relative" style={{ width: s.box, height: s.box }}>
        {/* Soft outer pulse */}
        <span
          aria-hidden
          className="absolute inset-0 rounded-full bg-blue-500/20 blur-xl animate-brand-pulse"
        />
        {/* Mid breathing halo */}
        <span
          aria-hidden
          className="absolute inset-1 rounded-full bg-gradient-to-br from-blue-500/15 via-cyan-400/10 to-emerald-400/15 animate-brand-breath"
        />
        {/* Thin rotating accent ring */}
        <span
          aria-hidden
          className={`absolute inset-0 rounded-full ${s.ring} border-transparent border-t-blue-500 border-r-cyan-400 animate-brand-spin`}
        />
        {/* Logo */}
        <div className="absolute inset-0 flex items-center justify-center">
          <img
            src="/logo.png"
            alt="ClinicalFlow"
            className="object-contain drop-shadow-[0_4px_14px_rgba(37,99,235,0.25)] animate-brand-float"
            style={{ width: s.logo, height: s.logo }}
            draggable={false}
          />
        </div>
      </div>
      {label && (
        <p className={`${s.text} font-medium text-slate-500 tracking-wide`}>
          {label}
        </p>
      )}
      <span className="sr-only">Loading…</span>
    </div>
  );

  if (fullScreen) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-white/70 backdrop-blur-sm"
        data-testid="brand-loader-fullscreen"
      >
        {container}
      </div>
    );
  }

  return container;
}

export default BrandLoader;
